import { ArrowRight } from 'lucide-react'

export type PendingKind = 'add' | 'change' | 'remove'

const BANNER: Record<PendingKind, { title: string; className: string }> = {
  add: {
    title: 'Pending addition — awaiting CAI approval',
    className: 'bg-cai-blue/10 text-cai-blue',
  },
  change: {
    title: 'Pending change — awaiting CAI approval',
    className: 'bg-cai-blue/10 text-cai-blue',
  },
  remove: {
    title: 'Pending removal — awaiting CAI approval',
    className: 'bg-error/10 text-error',
  },
}

export function PendingBanner({ kind }: { kind: PendingKind }) {
  const { title, className } = BANNER[kind]
  return <div className={`rounded-md px-3 py-2 text-sm font-medium ${className}`}>{title}</div>
}

export function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <div className="text-muted-foreground text-xs font-medium">{label}</div>
      {/* Wraps long unbroken values, such as a telematics ID, instead of running into the next column. */}
      <div className="text-sm wrap-anywhere">{value || <span className="text-muted-foreground">—</span>}</div>
    </div>
  )
}

// Shows a single field. When `newValue` is provided and differs from `oldValue`
// (a pending change), renders the old value struck through and the new value
// highlighted; otherwise renders the value plainly.
export function DiffRow({
  label,
  oldValue,
  newValue,
}: {
  label: string
  oldValue: string
  newValue?: string
}) {
  const changed = newValue !== undefined && oldValue !== newValue
  if (!changed) {
    return <DetailRow label={label} value={oldValue} />
  }
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <div className="text-muted-foreground text-xs font-medium">{label}</div>
      <div className="flex items-center gap-1.5 text-sm wrap-anywhere">
        <span className="text-muted-foreground line-through">{oldValue || '—'}</span>
        <ArrowRight className="text-muted-foreground size-3.5 shrink-0" />
        <span className="text-cai-blue font-medium">{newValue || '—'}</span>
      </div>
    </div>
  )
}
